import { Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { HelperResolution } from "utils/worksheet/worksheetHelpers";
import {
  useCalculationsDrawerData,
  useCalculationsSnapshot,
  useCostRowLinkToggles,
  useProductionFormSync,
} from "./useCalculationsDrawer";
import WorksheetProductionTab from "./WorksheetProductionTab";
import WorksheetRowLinkToggle from "./WorksheetRowLinkToggle";
import WorksheetTravelTab from "./WorksheetTravelTab";

type WorksheetCalculationsDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  saving?: boolean;
  isReadOnly?: boolean;
  resolution: HelperResolution;
};

export default function WorksheetCalculationsDrawer({
  open,
  onClose,
  onSave,
  saving,
  isReadOnly,
  resolution,
}: WorksheetCalculationsDrawerProps) {
  const { control, setValue } = useFormContext<WorksheetItemInput>();
  const values = useWatch({ control }) as WorksheetItemInput;
  const [tab, setTab] = useState(0);
  const production = values.helpers?.production;
  const travel = values.helpers?.travel;

  const { materialRows, materialOptions, hourlyRows, productionUnit } =
    useCalculationsDrawerData(values);
  const handleCancel = useCalculationsSnapshot(open, onClose);
  useProductionFormSync(values, productionUnit, resolution);
  const toggleLink = useCostRowLinkToggles(values);

  const renderToggle = (index: number, kind: "duration" | "travel") => {
    const row = values.costRows?.[index];

    if (!row) return null;

    const checked = (row.quantitySource ?? "").includes(kind);
    const helperEnabled = kind === "duration" ? !!production?.enabled : !!travel?.enabled;
    const disabled = isReadOnly || (!helperEnabled && !checked);

    return (
      <WorksheetRowLinkToggle
        key={`${kind}-${row.rowUuid}`}
        name={row.name ?? ""}
        rowUuid={`${kind}-${row.rowUuid}`}
        checked={checked}
        disabled={disabled}
        onChange={(nextEnabled) => toggleLink(index, kind, nextEnabled)}
      />
    );
  };

  return (
    <UiSideModal
      open={open}
      onClose={handleCancel}
      title="Calculations"
      width="498px"
      onConfirm={isReadOnly ? undefined : () => onSave?.()}
      confirmLabel="Save"
      confirmDisabled={saving}
    >
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="fullWidth"
        sx={{
          borderBottom: `1px solid ${draftColors.border}`,
          minWidth: 0,
          "& .MuiTabs-flexContainer": { minWidth: 0 },
          "& .MuiTab-root": {
            minWidth: 0,
            px: "8px",
            fontSize: "12px",
            textTransform: "none",
            whiteSpace: "nowrap",
          },
        }}
      >
        <Tab label={`Production and Job Time${production?.enabled ? " •" : ""}`} />
        <Tab label={`Travel${travel?.enabled ? " •" : ""}`} />
      </Tabs>

      <MDBox p="24px" display="flex" flexDirection="column" gap="16px" minWidth={0}>
        {tab === 0 && (
          <WorksheetProductionTab
            values={values}
            productionUnit={productionUnit}
            materialOptions={materialOptions}
            materialRowCount={materialRows.length}
            hourlyRows={hourlyRows}
            resolution={resolution}
            isReadOnly={isReadOnly}
            renderToggle={(index) => renderToggle(index, "duration")}
          />
        )}
        {tab === 1 && (
          <WorksheetTravelTab
            values={values}
            hourlyRows={hourlyRows}
            resolution={resolution}
            isReadOnly={isReadOnly}
            renderToggle={(index) => renderToggle(index, "travel")}
          />
        )}
      </MDBox>
    </UiSideModal>
  );
}
